import React from 'react'
import './Card.css'

export interface CardProps {
  children: React.ReactNode
  backgroundColor?: string
  selected?: boolean
  onClick?: () => void
  ariaLabel?: string
  className?: string
}

/**
 * Card Component
 *
 * Reusable card container following design guidelines
 *
 * Features:
 * - 12px border radius
 * - 16px padding
 * - Hover states with scale and shadow
 * - Selected state with border and shadow
 * - Configurable background color
 *
 * Used for:
 * - Brick type selector cards
 * - Color picker swatches
 * - Any card-based UI element
 *
 * @component
 */
export const Card: React.FC<CardProps> = ({
  children,
  backgroundColor,
  selected = false,
  onClick,
  ariaLabel,
  className = '',
}) => {
  const classNames = [
    'card',
    selected && 'card--selected',
    onClick && 'card--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const style: React.CSSProperties = backgroundColor
    ? { backgroundColor }
    : {}

  return (
    <div
      className={classNames}
      style={style}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      aria-pressed={selected}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {children}
    </div>
  )
}
